/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return */
import useLanguage from '../../../state/hooks/useLanguage'
import { useCitySelection } from '../hooks/useCitySelection'
import { City, CityGroup } from '../types'
import { CityGroupInfoDialog } from './CityGroupInfoDialog'
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Layers as LayersIcon,
  Info as InfoIcon,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'

interface CitySelectionDialogProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onSave: (selectedCities: number[]) => void
  readonly cities: City[]
  readonly cityGroups: CityGroup[]
  readonly initialSelection: number[]
  readonly title?: string
}

export function CitySelectionDialog({
  open,
  onClose,
  onSave,
  cities,
  cityGroups,
  initialSelection,
  title,
}: CitySelectionDialogProps) {
  const intl = useIntl()
  const { language } = useLanguage()
  const [searchQuery, setSearchQuery] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [countryFilter, setCountryFilter] = useState('')
  const [groupInfoModal, setGroupInfoModal] = useState<CityGroup | null>(null)

  const displayTitle =
    title ?? intl.formatMessage({ id: 'organizations.create.addCities' })

  const getLocalizedName = useCallback(
    (city: City): string => {
      const locale: string | undefined = language.locale
      const lang = city.lang
      if (lang?.name_new && locale) {
        const found = lang.name_new.find((i) => i.lang === locale)
        if (found) return found.value
      }
      return city.name
    },
    [language]
  )

  const getLocalizedCountry = useCallback(
    (city: City): string => {
      const locale: string | undefined = language.locale
      const lang = city.lang
      if (lang?.country_new && locale) {
        const found = lang.country_new.find((i) => i.lang === locale)
        if (found) return found.value
      }
      return city.country
    },
    [language]
  )

  const getLocalizedState = useCallback(
    (city: City): string => {
      const locale: string | undefined = language.locale
      const lang = city.lang
      if (lang?.state_new && locale) {
        const found = lang.state_new.find((i) => i.lang === locale)
        if (found) return found.value
      }
      return city.state
    },
    [language]
  )

  const {
    selectedCities,
    toggleCity,
    toggleGroup,
    isGroupChecked,
    isGroupIndeterminate,
    selectAll,
    deselectAll,
    setSelection,
  } = useCitySelection(initialSelection)

  useEffect(() => {
    if (open) {
      setSelection(initialSelection)
    }
  }, [open, initialSelection])

  const filteredCities = useMemo(
    () =>
      cities.filter((city) => {
        const localName = getLocalizedName(city).toLowerCase()
        const localState = getLocalizedState(city).toLowerCase()
        const localCountry = getLocalizedCountry(city).toLowerCase()
        const q = searchQuery.toLowerCase()
        const matchesSearch =
          localName.includes(q) ||
          localState.includes(q) ||
          localCountry.includes(q)
        const matchesState =
          !stateFilter || getLocalizedState(city) === stateFilter
        const matchesCountry =
          !countryFilter || getLocalizedCountry(city) === countryFilter
        return matchesSearch && matchesState && matchesCountry
      }),
    [
      cities,
      searchQuery,
      stateFilter,
      countryFilter,
      getLocalizedName,
      getLocalizedState,
      getLocalizedCountry,
    ]
  )

  const handleSave = () => {
    onSave(selectedCities)
    onClose()
  }

  const handleClose = () => {
    setSearchQuery('')
    setStateFilter('')
    setCountryFilter('')
    onClose()
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#31353D', backgroundImage: 'none' } }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {displayTitle}
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              placeholder={intl.formatMessage({
                id: 'organizations.cityModal.searchPlaceholder',
              })}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>
                    {intl.formatMessage({
                      id: 'organizations.cityTable.state',
                    })}
                  </InputLabel>
                  <Select
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    label={intl.formatMessage({
                      id: 'organizations.cityTable.state',
                    })}
                  >
                    <MenuItem key="all-states" value="">
                      <FormattedMessage id="organizations.cityModal.allStates" />
                    </MenuItem>
                    {Array.from(
                      new Set(
                        cities.map((c) => getLocalizedState(c)).filter(Boolean)
                      )
                    )
                      .sort((a, b) => a.localeCompare(b))
                      .map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>
                    {intl.formatMessage({
                      id: 'organizations.cityTable.country',
                    })}
                  </InputLabel>
                  <Select
                    value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}
                    label={intl.formatMessage({
                      id: 'organizations.cityTable.country',
                    })}
                  >
                    <MenuItem key="all-countries" value="">
                      <FormattedMessage id="organizations.cityModal.allCountries" />
                    </MenuItem>
                    {Array.from(
                      new Set(
                        cities
                          .map((c) => getLocalizedCountry(c))
                          .filter(Boolean)
                      )
                    )
                      .sort((a, b) => a.localeCompare(b))
                      .map((country) => (
                        <MenuItem key={country} value={country}>
                          {country}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box
              sx={{
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                maxHeight: 400,
                overflow: 'auto',
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        onChange={(e) => {
                          if (e.target.checked) {
                            selectAll(filteredCities.map((c) => c.id))
                          } else {
                            deselectAll(filteredCities.map((c) => c.id))
                          }
                        }}
                        checked={
                          filteredCities.length > 0 &&
                          filteredCities.every((c) =>
                            selectedCities.includes(c.id)
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {intl.formatMessage({
                        id: 'organizations.cityTable.name',
                      })}
                    </TableCell>
                    <TableCell>
                      {intl.formatMessage({
                        id: 'organizations.cityTable.state',
                      })}
                    </TableCell>
                    <TableCell>
                      {intl.formatMessage({
                        id: 'organizations.cityTable.country',
                      })}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cityGroups.map((group) => (
                    <TableRow
                      key={`group-${group.id}`}
                      sx={{ bgcolor: 'action.hover' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isGroupChecked(group)}
                          indeterminate={isGroupIndeterminate(group)}
                          onChange={() => toggleGroup(group)}
                        />
                      </TableCell>
                      <TableCell colSpan={3}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <LayersIcon color="primary" fontSize="small" />
                            <Box>
                              <Typography variant="body2">
                                {group.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {intl.formatMessage(
                                  { id: 'organizations.cityModal.groupCities' },
                                  { count: group.cities.length }
                                )}
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => setGroupInfoModal(group)}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredCities.map((city) => (
                    <TableRow key={city.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedCities.includes(city.id)}
                          onChange={() => toggleCity(city.id)}
                        />
                      </TableCell>
                      <TableCell>{getLocalizedName(city)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {getLocalizedState(city)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {getLocalizedCountry(city)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage(
                { id: 'organizations.cityModal.selected' },
                { count: selectedCities.length }
              )}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="organizations.users.form.cancel" />
          </Button>
          <Button variant="contained" onClick={handleSave}>
            <FormattedMessage id="organizations.users.form.saveChanges" />
          </Button>
        </DialogActions>
      </Dialog>

      <CityGroupInfoDialog
        group={groupInfoModal}
        cities={cities}
        onClose={() => setGroupInfoModal(null)}
      />
    </>
  )
}
